import type { MonitoringHandoffContext } from './monitoringLoop';

export interface MonitoringAutomationHandoffContext {
  handoff: 'monitoring-automation';
  source: 'dashboard' | 'portfolio-analytics' | 'risk-intelligence';
  proof: string;
  occurredAt?: string;
  goal?: string;
  drifted?: number;
  ruleFamily: 'portfolio-drift' | 'portfolio-watch' | 'risk-watch';
}

export interface AlertDeliveryRepairHandoffContext {
  handoff: 'alert-delivery-repair';
  source: 'alerts';
  proofKind: 'latest-fired' | 'active-coverage';
  proofMessage: string;
  blocker?: MonitoringHandoffContext['blocker'];
  ruleName?: string;
  triggerType?: string;
  firedAt?: string;
  acknowledgedAt?: string;
  enabledRuleCount?: number;
  coverageLabels?: string[];
  missingLabels?: string[];
}

const ALERT_DELIVERY_STORAGE_PREFIX = 'pinecurl.alert-delivery-repair';

function isMonitoringSource(value: string | null): value is MonitoringHandoffContext['source'] {
  return value === 'dashboard' || value === 'portfolio-analytics' || value === 'goals';
}

function isAutomationMonitoringSource(
  value: string | null,
): value is MonitoringAutomationHandoffContext['source'] {
  return value === 'dashboard' || value === 'portfolio-analytics' || value === 'risk-intelligence';
}

function isAutomationRuleFamily(
  value: string | null,
): value is MonitoringAutomationHandoffContext['ruleFamily'] {
  return value === 'portfolio-drift' || value === 'portfolio-watch' || value === 'risk-watch';
}

function isNotificationBlocker(
  value: string | null,
): value is NonNullable<MonitoringHandoffContext['blocker']> {
  return value === 'portfolioDrift' || value === 'push' || value === 'email' || value === 'digest';
}

function isAlertDeliveryProofKind(
  value: string | null,
): value is AlertDeliveryRepairHandoffContext['proofKind'] {
  return value === 'latest-fired' || value === 'active-coverage';
}

function canUseBrowserStorage() {
  return (
    typeof window !== 'undefined' &&
    typeof window.sessionStorage !== 'undefined' &&
    typeof window.localStorage !== 'undefined'
  );
}

function getAlertDeliveryStorageKey(userId: string) {
  return `${ALERT_DELIVERY_STORAGE_PREFIX}:${userId}`;
}

function getOptionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getOptionalTimestamp(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  return Number.isNaN(new Date(value).getTime()) ? undefined : value;
}

function getOptionalNonNegativeNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : Number.NaN;

  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function getOptionalStringArray(value: unknown): string[] | undefined {
  const values =
    Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split('|')
        : [];

  const cleaned = values
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter(Boolean);

  return cleaned.length > 0 ? cleaned : undefined;
}

function sanitizeAlertDeliveryRepairHandoff(value: unknown): AlertDeliveryRepairHandoffContext | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const raw = value as Partial<AlertDeliveryRepairHandoffContext> & {
    focus?: string | null;
    rule?: string | null;
    trigger?: string | null;
    activeRules?: string | number | null;
    coverage?: string | string[] | null;
    missing?: string | string[] | null;
  };

  const proofKind =
    typeof raw.proofKind === 'string'
      ? raw.proofKind
      : null;
  const proofMessage = getOptionalText(raw.proofMessage);

  if (
    raw.handoff !== 'alert-delivery-repair' ||
    raw.source !== 'alerts' ||
    !isAlertDeliveryProofKind(proofKind) ||
    !proofMessage
  ) {
    return null;
  }

  const blocker = getOptionalText(raw.blocker ?? raw.focus);
  const sanitizedBlocker = isNotificationBlocker(blocker ?? null) ? blocker : undefined;

  return {
    handoff: 'alert-delivery-repair',
    source: 'alerts',
    proofKind,
    proofMessage,
    blocker: sanitizedBlocker as AlertDeliveryRepairHandoffContext['blocker'],
    ruleName: getOptionalText(raw.ruleName ?? raw.rule),
    triggerType: getOptionalText(raw.triggerType ?? raw.trigger),
    firedAt: getOptionalTimestamp(raw.firedAt),
    acknowledgedAt: getOptionalTimestamp(raw.acknowledgedAt),
    enabledRuleCount: getOptionalNonNegativeNumber(raw.enabledRuleCount ?? raw.activeRules),
    coverageLabels: getOptionalStringArray(raw.coverageLabels ?? raw.coverage),
    missingLabels: getOptionalStringArray(raw.missingLabels ?? raw.missing),
  };
}

function setIfPresent(params: URLSearchParams, key: string, value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return;
  params.set(key, String(value));
}

export function getMonitoringHandoffPath(context: MonitoringHandoffContext): string {
  if (context.destination === 'automation') {
    const params = new URLSearchParams();
    params.set('handoff', 'monitoring-automation');
    params.set('source', context.source);
    params.set('proof', context.proofLabel);
    setIfPresent(params, 'occurredAt', context.proofOccurredAt);
    setIfPresent(params, 'goal', context.activeGoalName);
    setIfPresent(params, 'drifted', context.driftedAssetCount);
    params.set('ruleFamily', context.ruleFamily ?? 'portfolio-watch');
    return `/automation?${params.toString()}`;
  }

  const params = new URLSearchParams();
  params.set('monitoring', 'goal-closure');
  params.set('source', context.source);
  params.set('goal', context.activeGoalName);
  params.set('targets', String(context.activeTargetCount));
  params.set('drifted', String(context.driftedAssetCount));
  params.set('proof', context.proofLabel);
  setIfPresent(params, 'occurredAt', context.proofOccurredAt);

  if (context.destination === 'alerts') {
    params.set('tab', 'rules');
    params.set('focus', 'create-rule');
    params.set('recommended', context.recommendedRuleType ?? 'position_change');
    return `/alerts?${params.toString()}`;
  }

  params.set('focus', context.blocker ?? 'portfolioDrift');
  return `/notifications?${params.toString()}`;
}

export function getAlertDeliveryRepairPath(
  context: Omit<AlertDeliveryRepairHandoffContext, 'handoff' | 'source'>,
): string {
  const params = new URLSearchParams();
  params.set('alertDelivery', 'repair');
  params.set('source', 'alerts');
  params.set('proofKind', context.proofKind);
  params.set('proofMessage', context.proofMessage);
  setIfPresent(params, 'focus', context.blocker);
  setIfPresent(params, 'rule', context.ruleName);
  setIfPresent(params, 'trigger', context.triggerType);
  setIfPresent(params, 'firedAt', context.firedAt);
  setIfPresent(params, 'acknowledgedAt', context.acknowledgedAt);
  setIfPresent(params, 'activeRules', context.enabledRuleCount);
  setIfPresent(params, 'coverage', context.coverageLabels?.join('|'));
  setIfPresent(params, 'missing', context.missingLabels?.join('|'));
  return `/settings/notifications?${params.toString()}`;
}

export function readMonitoringHandoff(searchParams: URLSearchParams): MonitoringHandoffContext | null {
  if (searchParams.get('monitoring') !== 'goal-closure') {
    return null;
  }

  const source = searchParams.get('source');
  const goal = searchParams.get('goal');
  if (!isMonitoringSource(source) || !goal) {
    return null;
  }

  const recommended = searchParams.get('recommended');
  const focus = searchParams.get('focus');
  const tab = searchParams.get('tab');

  return {
    source,
    destination: tab === 'rules' ? 'alerts' : 'notifications',
    activeGoalName: goal,
    activeTargetCount: Number(searchParams.get('targets') ?? 0),
    driftedAssetCount: Number(searchParams.get('drifted') ?? 0),
    proofLabel: searchParams.get('proof') ?? goal,
    proofOccurredAt: searchParams.get('occurredAt'),
    recommendedRuleType:
      tab === 'rules' && recommended === 'position_change' ? 'position_change' : undefined,
    blocker:
      tab === 'rules'
        ? undefined
        : (focus as MonitoringHandoffContext['blocker'] | null) ?? undefined,
  };
}

export function readAlertDeliveryRepairHandoff(
  searchParams: URLSearchParams,
): AlertDeliveryRepairHandoffContext | null {
  if (searchParams.get('alertDelivery') !== 'repair' || searchParams.get('source') !== 'alerts') {
    return null;
  }

  return sanitizeAlertDeliveryRepairHandoff({
    handoff: 'alert-delivery-repair',
    source: 'alerts',
    proofKind: searchParams.get('proofKind'),
    proofMessage: searchParams.get('proofMessage'),
    focus: searchParams.get('focus'),
    rule: searchParams.get('rule'),
    trigger: searchParams.get('trigger'),
    firedAt: searchParams.get('firedAt'),
    acknowledgedAt: searchParams.get('acknowledgedAt'),
    activeRules: searchParams.get('activeRules'),
    coverage: searchParams.get('coverage'),
    missing: searchParams.get('missing'),
  });
}

function readStoredAlertDeliveryRepairHandoff(
  storage: Storage | null,
  userId: string,
): AlertDeliveryRepairHandoffContext | null {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(getAlertDeliveryStorageKey(userId));
    if (!raw) {
      return null;
    }

    return sanitizeAlertDeliveryRepairHandoff(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function readPersistedAlertDeliveryRepairHandoff(
  userId?: string | null,
): AlertDeliveryRepairHandoffContext | null {
  if (!userId || !canUseBrowserStorage()) {
    return null;
  }

  return (
    readStoredAlertDeliveryRepairHandoff(window.sessionStorage, userId) ??
    readStoredAlertDeliveryRepairHandoff(window.localStorage, userId)
  );
}

export function writePersistedAlertDeliveryRepairHandoff(
  userId: string | null | undefined,
  handoff: AlertDeliveryRepairHandoffContext | null,
) {
  if (!userId || !canUseBrowserStorage()) {
    return;
  }

  const key = getAlertDeliveryStorageKey(userId);

  try {
    if (!handoff) {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
      return;
    }

    const sanitized = sanitizeAlertDeliveryRepairHandoff(handoff);
    if (!sanitized) {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
      return;
    }

    const serialised = JSON.stringify(sanitized);
    window.sessionStorage.setItem(key, serialised);
    window.localStorage.setItem(key, serialised);
  } catch {
    // noop
  }
}

export function readMonitoringAutomationHandoff(
  searchParams: URLSearchParams,
): MonitoringAutomationHandoffContext | null {
  if (searchParams.get('handoff') !== 'monitoring-automation') {
    return null;
  }

  const source = searchParams.get('source');
  const proof = searchParams.get('proof')?.trim();
  const ruleFamily = searchParams.get('ruleFamily');
  const occurredAt = searchParams.get('occurredAt');
  const goal = searchParams.get('goal')?.trim();
  const driftedRaw = searchParams.get('drifted');

  if (!isAutomationMonitoringSource(source) || !proof || !isAutomationRuleFamily(ruleFamily)) {
    return null;
  }

  if (occurredAt && Number.isNaN(new Date(occurredAt).getTime())) {
    return null;
  }

  let drifted: number | undefined;
  if (driftedRaw !== null) {
    const parsed = Number(driftedRaw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }
    drifted = parsed;
  }

  if (!goal && drifted === undefined) {
    return null;
  }

  return {
    handoff: 'monitoring-automation',
    source,
    proof,
    occurredAt: occurredAt ?? undefined,
    goal: goal || undefined,
    drifted,
    ruleFamily,
  };
}
